// packages/ui/src/components/Pagination.tsx

import React from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { Button } from './Button';
import { IconButton } from './IconButton';

/**
 * Generates the pagination range with ellipsis.
 * @param currentPage - The current active page (0-indexed).
 * @param totalPages - The total number of pages.
 * @param siblingCount - The number of pages to show on each side of the current page.
 * @returns An array of page numbers and ellipsis strings.
 */
const generatePaginationRange = (
  currentPage: number,
  totalPages: number,
  siblingCount: number = 1
): (number | '...')[] => {
  const totalPageNumbers = siblingCount + 5; // siblingCount + firstPage + lastPage + currentPage + 2*ellipsis

  // Case 1: If the number of pages is less than the page numbers we want to show.
  if (totalPageNumbers >= totalPages) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 0);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages - 1);

  const shouldShowLeftDots = leftSiblingIndex > 1;
  const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

  const firstPageIndex = 0;
  const lastPageIndex = totalPages - 1;

  // Case 2: No left dots to show, but right dots to be shown.
  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftItemCount }, (_, i) => i);
    return [...leftRange, '...', lastPageIndex];
  }

  // Case 3: No right dots to show, but left dots to be shown.
  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = Array.from({ length: rightItemCount }, (_, i) => lastPageIndex - rightItemCount + 1 + i);
    return [firstPageIndex, '...', ...rightRange];
  }

  // Case 4: Both left and right dots to be shown.
  if (shouldShowLeftDots && shouldShowRightDots) {
    const middleRange = Array.from({ length: rightSiblingIndex - leftSiblingIndex + 1 }, (_, i) => leftSiblingIndex + i);
    return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
  }

  return []; // Should be unreachable
};


interface PaginationProps {
  totalCount: number;
  pageSize: number;
  currentPage: number; // 0-indexed
  onPageChange: (page: number) => void;
  siblingCount?: number;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  totalCount,
  pageSize,
  currentPage,
  onPageChange,
  siblingCount = 1,
  className,
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);

  if (totalPages <= 1) {
    return null; // Don't render pagination if there's only one page
  }

  const paginationRange = generatePaginationRange(currentPage, totalPages, siblingCount);

  const onNext = () => {
    if (currentPage < totalPages - 1) {
      onPageChange(currentPage + 1);
    }
  };

  const onPrevious = () => {
    if (currentPage > 0) {
      onPageChange(currentPage - 1);
    }
  };

  return (
    <nav aria-label="Pagination" className={clsx('flex items-center justify-center gap-2', className)}>
      <IconButton
        icon={<ChevronLeft size={16} />}
        ariaLabel="Go to previous page"
        onClick={onPrevious}
        disabled={currentPage === 0}
        variant="outline"
        size="sm"
      />

      {paginationRange.map((pageNumber, index) => {
        if (pageNumber === '...') {
          return (
            <div key={`dots-${index}`} className="flex items-center justify-center h-9 w-9 text-slate-500 [.theme-liquid-glass_&]:text-white/60">
              <MoreHorizontal size={16} />
            </div>
          );
        }

        return (
          <Button
            key={pageNumber}
            text={String(pageNumber + 1)} // Display as 1-indexed
            variant={pageNumber === currentPage ? 'primary' : 'outline'}
            size="sm"
            onClick={() => onPageChange(pageNumber as number)}
            className="!p-0 h-9 w-9" // Use exact dimensions for square buttons
            aria-current={pageNumber === currentPage ? 'page' : undefined}
          />
        );
      })}

      <IconButton
        icon={<ChevronRight size={16} />}
        ariaLabel="Go to next page"
        onClick={onNext}
        disabled={currentPage === totalPages - 1}
        variant="outline"
        size="sm"
      />
    </nav>
  );
};
